import React, { ReactNode, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { X } from 'lucide-react'
import styled from 'styled-components'
import PixelSpinner from '../pixel-spinner/pixel-spinner'
import { colors } from '../styleGuide'

// Styled Components
const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background-color: rgba(46, 49, 74, 0.4);
  z-index: 40;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: opacity 800ms ease-in-out; /* Slow fade in/out for backdrop */
`

const SliderPanel = styled.div<{
  $isOpen: boolean
  $width: string
  $customWith?: string
}>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: ${(props) =>
    isMobile ? (props.$customWith ? props.$customWith : '100%') : props.$width};
  background-color: ${colors.white};
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  transform: ${(props) =>
    props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94); /* Slow slide with smooth easing */
`

const Header = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11.8px;
  border-bottom: 1px solid ${colors.grayBorderLight};
  background-color: ${colors.white};
  margin-left: ${(props) => (props.isMobile ? '20px' : '0')};
`

const Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  margin: 0;
`

const CloseButton = styled.button`
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${colors.grayLightBg};
  }

  &:focus {
    outline: 2px solid ${colors.blueLight};
    outline-offset: 2px;
  }
`

const Content = styled.div<{ padding?: string }>`
  flex: 1;
  /* overflow-y: auto; */
  padding: ${(props) => props.padding || '20px'};
  background-color: ${colors.grayLightBg};
  position: relative;
  z-index: 1;
`

interface DcOverlaySliderProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  width?: string
  padding?: string
  loading?: boolean
  customWith?: string
}

export const DcOverlaySlider = React.forwardRef<
  HTMLDivElement,
  DcOverlaySliderProps
>((props, ref) => {
  const {
    isOpen,
    onClose,
    title,
    children,
    padding,
    width = '70%',
    loading,
    customWith,
  } = props
  // Handle escape key press to close the slider
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)

    // Prevent scrolling on the body when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = 'auto'
    }
  }, [isOpen, onClose])

  // Handle close button click with event prevention
  const handleCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onClose()
  }

  return (
    <>
      {/* Backdrop overlay - always rendered but with opacity transition */}
      <Backdrop
        $isOpen={isOpen}
        onClick={onClose}
        aria-hidden="true"
        style={{
          pointerEvents: isOpen ? 'auto' : 'none',
          visibility: isOpen ? 'visible' : 'hidden',
        }}
      />

      {/* Slider panel - always rendered but with transform transition */}
      <SliderPanel
        $isOpen={isOpen}
        $width={width}
        $customWith={customWith}
        ref={ref}
        // role="dialog"
        // aria-modal="true"
        // aria-labelledby="slider-title"
        style={{
          visibility: 'visible', // Always visible to allow transition
        }}
      >
        {/* Header */}
        {loading ? (
          <PixelSpinner isShow={true} />
        ) : (
          <>
            <Header isMobile={isMobile}>
              <Title id="slider-title">{title}</Title>
              <CloseButton
                type="button"
                onClick={handleCloseClick}
                aria-label="Close"
              >
                <X size={20} />
              </CloseButton>
            </Header>

            <Content padding={padding}>{children}</Content>
          </>
        )}
      </SliderPanel>
    </>
  )
})

export default DcOverlaySlider
